import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import {
  atualizarValoresTransacao,
  fetchHistoricoPublicacao,
  fetchProdutosPublicacao,
  publicacaoKeys,
} from '@/services/publicacaoService';
import { parseApiError } from '@/utils/apiError';
import { Button } from '@/components/ui/button';
import { Empty, EmptyDescription, EmptyTitle } from '@/components/ui/empty';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Item, ItemContent, ItemGroup, ItemTitle } from '@/components/ui/item';
import { Spinner } from '@/components/ui/spinner';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { historicoSearchSchema, itensFormSchema, type THistoricoSearchFormData, type TItensFormData } from './schema';
import type { IHistoricoPublicacao, TTipoTransacao } from '@/types/publicacao';

// ponytail: flip to false to re-enable Atualizar Transações Publicadas
const ATUALIZAR_TRANSACOES_DISABLED = true;

export const Route = createFileRoute('/_authenticated/atualizar-transacoes/')({
  component: ATUALIZAR_TRANSACOES_DISABLED ? AtualizarTransacoesDisabled : AtualizarTransacoes,
});

function AtualizarTransacoesDisabled() {
  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-2xl font-semibold text-on-surface">Atualizar Transações Publicadas</h2>
        <p className="text-on-surface-variant">
          Esta funcionalidade está temporariamente desabilitada.
        </p>
      </div>
      <Empty>
        <EmptyTitle>Indisponível no momento</EmptyTitle>
        <EmptyDescription>
          A atualização de valores de transações já publicadas no MAPA foi desabilitada temporariamente.
        </EmptyDescription>
      </Empty>
    </div>
  );
}

function toStr(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return '';
}

function formatarData(dataString: string | null | undefined): string {
  if (!dataString) return '-';
  return new Date(dataString).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const tipos: TTipoTransacao[] = ['PRODUCAO', 'VENDA', 'COMPRA', 'ABATE'];
const tipoLabels: Record<TTipoTransacao, string> = {
  PRODUCAO: 'Produção',
  VENDA: 'Venda',
  COMPRA: 'Compra',
  ABATE: 'Abate',
};

function AtualizarTransacoes() {
  const [tipoTransacao, setTipoTransacao] = useState<TTipoTransacao>('PRODUCAO');
  const [transacaoSelecionada, setTransacaoSelecionada] = useState<IHistoricoPublicacao | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const { control: searchControl, register: registerSearch } = useForm<THistoricoSearchFormData>({
    resolver: zodResolver(historicoSearchSchema),
    defaultValues: { search: '' },
  });
  const searchInput = useWatch({ control: searchControl, name: 'search' });

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<TItensFormData>({
    resolver: zodResolver(itensFormSchema),
    defaultValues: { itens: [] },
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'itens' });

  useEffect(() => {
    setTransacaoSelecionada(null);
    reset({ itens: [] });
    setPage(1);
  }, [tipoTransacao, reset]);

  const filtros = { tipo_publicacao: tipoTransacao, sucesso: true, search: debouncedSearch, page, page_size: pageSize };
  const {
    data: historicoData,
    isLoading: loadingHistorico,
    refetch: refetchHistorico,
  } = useQuery({
    queryKey: publicacaoKeys.historico(filtros),
    queryFn: () => fetchHistoricoPublicacao(filtros),
  });
  const historico = historicoData?.items ?? [];
  const totalPages = historicoData?.total_pages ?? 0;

  const produtosQuery = useQuery({
    queryKey: publicacaoKeys.produtos(transacaoSelecionada?.id_mapa ?? ''),
    queryFn: () => fetchProdutosPublicacao(transacaoSelecionada!.id_mapa!),
    enabled: Boolean(transacaoSelecionada?.id_mapa),
  });

  useEffect(() => {
    if (!produtosQuery.data) return;

    const produtosRaw = produtosQuery.data.produtos;
    const produtos = Array.isArray(produtosRaw) ? produtosRaw : [];

    const itensCarregados: TItensFormData['itens'] = produtos.map((prod) => ({
      codigo: toStr(tipoTransacao === 'COMPRA' ? (prod.cod_produto ?? prod.cod_mapa ?? prod.codigo) : (prod.cod_mapa ?? prod.cod_produto ?? prod.codigo)),
      valor: '',
      descricao: toStr(prod.descricao ?? prod.nome ?? prod.produto),
    }));

    reset({ itens: itensCarregados });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [produtosQuery.data]);

  const updateMutation = useMutation({
    mutationFn: ({ tipo, idMapa, payload }: { tipo: TTipoTransacao; idMapa: string; payload: Parameters<typeof atualizarValoresTransacao>[2] }) =>
      atualizarValoresTransacao(tipo, idMapa, payload),
  });

  const handleSelecionarTransacao = (transacao: IHistoricoPublicacao) => {
    setTransacaoSelecionada(transacao);
    reset({ itens: [] });
  };

  const handleAdicionarItem = () => {
    append({ codigo: '', valor: '' });
  };

  const onSubmit = async (data: TItensFormData) => {
    if (!transacaoSelecionada?.id_mapa) return;

    try {
      const payload =
        tipoTransacao === 'COMPRA'
          ? { itens: data.itens.map((item) => ({ cod_produto: item.codigo, quantidade: parseFloat(item.valor) })) }
          : { itens: data.itens.map((item) => ({ cod_mapa: item.codigo, total: parseFloat(item.valor) })) };

      const result = await updateMutation.mutateAsync({
        tipo: tipoTransacao,
        idMapa: transacaoSelecionada.id_mapa,
        payload,
      });

      if (result.success) {
        toast.success(`${result.message} - ${result.registros_atualizados_local} registro(s) atualizado(s).`);
        reset({ itens: [] });
      } else {
        toast.error(result.message || 'Erro ao atualizar transação');
      }
    } catch (err) {
      toast.error(`Erro: ${parseApiError(err)}`);
    }
  };

  const loadingProdutos = produtosQuery.isFetching;
  const loading = updateMutation.isPending;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-2xl font-semibold text-on-surface">Atualizar Transações Publicadas</h2>
        <p className="text-on-surface-variant">
          Atualize os valores de transações já publicadas no MAPA (Produção, Venda, Compra ou Abate).
        </p>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-sm font-medium text-on-surface-variant">Tipo de Transação:</span>
        <ToggleGroup
          type="single"
          variant="outline"
          value={tipoTransacao}
          onValueChange={(value) => {
            if (value) setTipoTransacao(value as TTipoTransacao);
          }}
        >
          {tipos.map((tipo) => (
            <ToggleGroupItem
              key={tipo}
              value={tipo}
            >
              {tipoLabels[tipo]}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="flex flex-col gap-4 rounded-lg border border-outline-variant p-4">
          <h3 className="text-lg font-semibold text-on-surface">Histórico de Publicações - {tipoTransacao}</h3>

          <div className="flex gap-2">
            <Input
              type="text"
              placeholder="Buscar por mês, ID…"
              autoComplete="off"
              className="flex-1"
              {...registerSearch('search')}
            />
            <Button
              type="button"
              onClick={() => void refetchHistorico()}
              disabled={loadingHistorico}
            >
              {loadingHistorico ? (
                <>
                  <Spinner data-icon="inline-start" />
                  Carregando...
                </>
              ) : (
                'Atualizar'
              )}
            </Button>
          </div>

          <div className="flex max-h-96 flex-col gap-2 overflow-y-auto">
            {loadingHistorico ? (
              <div className="p-4 text-center text-on-surface-variant">Carregando histórico...</div>
            ) : historico.length === 0 ? (
              <Empty>
                <EmptyTitle>Nenhuma publicação encontrada</EmptyTitle>
                <EmptyDescription>Nenhuma publicação de {tipoTransacao.toLowerCase()} encontrada com sucesso.</EmptyDescription>
              </Empty>
            ) : (
              <ItemGroup>
                {historico.map((item) => (
                  <Item
                    key={item.id}
                    asChild
                    variant={transacaoSelecionada?.id === item.id ? 'outline' : 'default'}
                    className={transacaoSelecionada?.id === item.id ? 'bg-primary-container' : 'hover:bg-surface-container'}
                  >
                    <button
                      type="button"
                      onClick={() => handleSelecionarTransacao(item)}
                    >
                      <ItemContent>
                        <ItemHeader item={item} />
                        <div className="flex items-center justify-between text-sm text-on-surface-variant">
                          <span>Mês: {item.mes}</span>
                          <span>{formatarData(item.data_publicacao || item.data_fim)}</span>
                        </div>
                      </ItemContent>
                    </button>
                  </Item>
                ))}
              </ItemGroup>
            )}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4">
              <Button
                variant="outline"
                onClick={() => setPage((p) => p - 1)}
                disabled={page <= 1}
              >
                Anterior
              </Button>
              <span className="text-sm text-on-surface-variant">
                {page} / {totalPages}
              </span>
              <Button
                variant="outline"
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= totalPages}
              >
                Próxima
              </Button>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-4 rounded-lg border border-outline-variant p-4">
          <h3 className="text-lg font-semibold text-on-surface">Atualizar Valores</h3>

          {transacaoSelecionada ? (
            <div className="flex flex-col gap-1 rounded-lg bg-surface-container p-3 text-sm">
              <span className="font-medium text-on-surface-variant">Transação Selecionada:</span>
              <span className="text-on-surface">ID MAPA: {transacaoSelecionada.id_mapa}</span>
              <span className="text-on-surface">Mês: {transacaoSelecionada.mes}</span>
              <span className="text-on-surface">Tipo: {transacaoSelecionada.tipo_publicacao}</span>
            </div>
          ) : (
            <div className="p-4 text-center text-on-surface-variant">
              Selecione uma transação do histórico para atualizar os valores.
            </div>
          )}

          {transacaoSelecionada && (
            <form
              onSubmit={(e) => void handleSubmit(onSubmit)(e)}
              noValidate
              className="flex flex-col gap-4"
            >
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-on-surface">
                    Produtos {loadingProdutos && <span className="text-sm font-normal text-on-surface-variant">(Carregando...)</span>}
                  </h4>
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    onClick={handleAdicionarItem}
                    disabled={loadingProdutos}
                  >
                    Adicionar Item
                  </Button>
                </div>

                {errors.itens?.root && <FieldError errors={[errors.itens.root]} />}

                {loadingProdutos ? (
                  <div className="p-4 text-center text-on-surface-variant">Buscando produtos da transação...</div>
                ) : fields.length === 0 ? (
                  <Empty>
                    <EmptyTitle>Nenhum produto encontrado</EmptyTitle>
                    <EmptyDescription>Clique em &quot;Adicionar Item&quot; para incluir manualmente.</EmptyDescription>
                  </Empty>
                ) : (
                  <div className="flex flex-col gap-3">
                    {fields.map((field, index) => {
                      const isCompra = tipoTransacao === 'COMPRA';
                      const itemErrors = errors.itens?.[index];

                      return (
                        <div
                          key={field.id}
                          className="flex flex-wrap items-end gap-3 rounded-lg border border-outline-variant p-3"
                        >
                          <span className="text-sm text-on-surface-variant">#{index + 1}</span>

                          <Field
                            className="flex-1"
                            data-invalid={!!itemErrors?.codigo}
                          >
                            <FieldLabel htmlFor={`itens.${index}.codigo`}>{isCompra ? 'Cód. Produto:' : 'Cód. MAPA:'}</FieldLabel>
                            <Input
                              id={`itens.${index}.codigo`}
                              placeholder={isCompra ? 'Ex: 28984' : 'Ex: 29131'}
                              readOnly={!!field.descricao}
                              autoComplete="off"
                              aria-invalid={!!itemErrors?.codigo}
                              {...register(`itens.${index}.codigo`)}
                            />
                            {field.descricao && <span className="text-xs text-on-surface-variant">{field.descricao}</span>}
                            <FieldError errors={itemErrors?.codigo ? [itemErrors.codigo] : undefined} />
                          </Field>

                          <Field
                            className="w-32"
                            data-invalid={!!itemErrors?.valor}
                          >
                            <FieldLabel htmlFor={`itens.${index}.valor`}>{isCompra ? 'Quantidade:' : 'Total:'}</FieldLabel>
                            <Input
                              id={`itens.${index}.valor`}
                              type="number"
                              step="0.01"
                              placeholder="Informe o valor"
                              autoComplete="off"
                              aria-invalid={!!itemErrors?.valor}
                              {...register(`itens.${index}.valor`)}
                            />
                            <FieldError errors={itemErrors?.valor ? [itemErrors.valor] : undefined} />
                          </Field>

                          <Button
                            variant="destructive"
                            size="sm"
                            type="button"
                            onClick={() => remove(index)}
                            title="Remover item"
                          >
                            Remover
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div>
                <Button
                  type="submit"
                  disabled={loading || fields.length === 0}
                >
                  {loading ? (
                    <>
                      <Spinner data-icon="inline-start" />
                      Atualizando...
                    </>
                  ) : (
                    `Atualizar ${tipoTransacao}`
                  )}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function ItemHeader({ item }: { item: IHistoricoPublicacao }) {
  return (
    <div className="flex items-center justify-between">
      <ItemTitle>ID MAPA: {item.id_mapa}</ItemTitle>
      <span className="rounded-full bg-primary px-2.5 py-1 text-xs font-medium text-on-primary">{item.tipo_publicacao}</span>
    </div>
  );
}
